import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText
} from 'ui/mui';
import { fetchWithAuth } from '../utils/auth';

const FulfilmentPickView = () => {
  const { bucket } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [bucket]);

  const fetchOrders = async () => {
    try {
      const response = await fetchWithAuth(`/api/fulfilment/get-pick-orders?bucket=${bucket}`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePickToggle = async (orderId, picked) => {
    if (picked) return; // Already picked

    try {
      const response = await fetchWithAuth('/api/fulfilment/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, type: 'PICK' }),
      });

      if (response.ok) {
        // Update local state
        setOrders(orders.map(order => 
          order.order_id === orderId 
            ? { ...order, picked: true }
            : order
        ));
      }
    } catch (err) {
      console.error('Pick error:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Pick View - {bucket?.replace('_', ' ')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tap to mark orders as picked. Sorted by lot number.
      </Typography>

      {orders.map((order) => (
        <Card key={order.order_id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  Order {order.order_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.buyer_username}
                </Typography>
                
                <List dense sx={{ mt: 1 }}>
                  {order.items?.map((item, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemText
                        primary={`Lot #${item.lot_number}: ${item.title}`}
                        secondary={`Qty: ${item.quantity}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box sx={{ ml: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={order.picked}
                      onChange={() => handlePickToggle(order.order_id, order.picked)}
                      disabled={order.picked}
                    />
                  }
                  label={order.picked ? "Picked" : "Pick"}
                />
                {order.picked && (
                  <Chip label="✓ Done" color="success" size="small" />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {orders.length === 0 && (
        <Alert severity="info">
          No orders found for {bucket}. All orders may be picked or there might be no pending orders.
        </Alert>
      )}
    </Box>
  );
};

export default FulfilmentPickView; 
